import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import { latestPrescreenNote } from '@/modules/candidate-detail/utils/prescreenCallNote';
import { useCandidateJobNotesQuery } from '@/modules/candidates/queries/useCandidateJobNotesQuery';
import { DroppedNoteLinks } from '@/modules/notes/components/DroppedNoteLinks';
import { NoteText } from '@/modules/notes/components/NoteText';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { actionErrorMessage } from '@/utils/actionErrorMessage';
import { formatDateLabel } from '@/utils/dateUtils';

import { CandidateSectionTitle } from './CandidateSectionTitle';

interface CandidatePrescreenCallNotesProps {
  candidateJobId: string;
}

/**
 * The complete pre-screen note, collapsed under the assessment built from it.
 * The ledger condenses the call, so this is where the hiring manager goes
 * when the assessment leaves something out. The notes load with the tab so
 * the section only appears when there is a note to show; the section owns
 * its own failure, since the brief above it has already rendered.
 */
export function CandidatePrescreenCallNotes({
  candidateJobId,
}: CandidatePrescreenCallNotesProps) {
  const [isOpen, setIsOpen] = useState(false);
  const notes = useCandidateJobNotesQuery(candidateJobId);

  if (notes.isPending) return null;

  if (notes.isError) {
    return (
      <InlineError>
        {actionErrorMessage('load the pre-screen notes', notes.error)}
      </InlineError>
    );
  }

  const note = latestPrescreenNote(notes.data);
  if (!note) return null;

  return (
    <section
      aria-labelledby="prescreen-notes-heading"
      className="flex flex-col gap-3"
    >
      <CandidateSectionTitle
        hint="Everything noted on the pre-screen call. The assessment above summarises these."
        id="prescreen-notes-heading"
      >
        Full pre-screen notes
      </CandidateSectionTitle>
      <button
        aria-controls="prescreen-notes"
        aria-expanded={isOpen}
        className="text-primary-700 flex w-fit cursor-pointer items-center gap-1 text-[13px] font-medium"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        {isOpen ? 'Hide pre-screen notes' : 'Show pre-screen notes'}
        <ChevronDown
          aria-hidden
          className={cn(
            'size-3.5 transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </button>
      {isOpen && (
        <div className="flex flex-col gap-2" id="prescreen-notes">
          <NoteText
            className="max-w-[64ch] text-[15px] leading-6 text-inherit"
            text={note.content}
          />
          {note.rawContent?.trim() && (
            <DroppedNoteLinks
              original={note.rawContent}
              polished={note.content}
            />
          )}
          <p className="text-[13px] text-neutral-500">
            {formatDateLabel(note.createdAt, 'Recorded')}
          </p>
        </div>
      )}
    </section>
  );
}
